import { AddCircle, Delete, Restore } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IFeatureAttributeDto } from "App/Typings/AbacFeatureAttribute";
import { useGetFeatureVariantsQuery } from "Pages/ABAC/AbacAppQueries";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FeatureDetailsState } from "../State/FeatureDetailsState";

export function ManageFeatureVariations({
  featureId,
  setVariations,
  fieldErrorMessage,
}: {
  featureId: string;
  setVariations: (variations: IFeatureAttributeDto[]) => void;
  fieldErrorMessage: FeatureDetailsState["getters"]["formik"]["errors"];
}) {
  const { data: featureVariants, isLoading } = useGetFeatureVariantsQuery(featureId);
  const [draftVariations, setDraftVariations] = useState<IFeatureAttributeDto[]>([]);

  const addVariation = () => {
    const newAttribute = {
      id: `new-${Date.now()}`,
      featureId,
      attributeId: "00000000-0000-0000-0000-000000000000",
      distribution: 0,
      attributeName: "",
      attributeDescription: "",
      attributeKey: null,
    };
    setDraftVariations((prev) => [...prev, newAttribute]);
  };

  const removeVariation = (id: string) => {
    setDraftVariations((prev) => prev.filter((v) => v.id !== id));
  };

  const resetVariations = () => {
    setDraftVariations(featureVariants?.defaultFeatureAttributes ?? []);
  };

  const editVariation = (attribute: IFeatureAttributeDto) => {
    setDraftVariations((prev) => {
      const index = prev.findIndex((attr) => attr.id === attribute.id);
      if (index !== -1) {
        const updatedVariations = [...prev];
        updatedVariations[index] = { ...updatedVariations[index], ...attribute };
        return updatedVariations;
      }
      // If not found, just return the previous state
      return prev;
    });
  };

  useEffect(() => {
    if (featureVariants && !isLoading) {
      const loadedVariations = featureVariants.defaultFeatureAttributes ?? [];
      setVariations(loadedVariations);
      setDraftVariations(loadedVariations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureVariants, isLoading]);

  useEffect(
    () => {
      setVariations(draftVariations);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(draftVariations)]
  );

  return isLoading ? null : (
    <Box marginBottom={2}>
      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr", lg: "1fr 1fr 1fr" } }}
        gap={2}
        alignItems="center"
        mb={2}
      >
        {draftVariations.map((variation) => {
          return (
            <Card key={variation.id} variant="elevation">
              <CardContent>
                <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                  <TextField
                    label="Name"
                    fullWidth
                    margin="normal"
                    size="small"
                    name={`assignedAttributes.${variation.id}.attributeName`}
                    value={variation.attributeName}
                    onChange={(e) => {
                      editVariation({ ...variation, attributeName: e.target.value });
                    }}
                    sx={{ flexGrow: { xs: 1, md: 3 }, width: { xs: "100%", md: "auto" } }}
                  />
                  <TextField
                    name={`assignedAttributes.${variation.id}.distribution`}
                    label="Distribution (%)"
                    type="number"
                    fullWidth
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                    value={variation.distribution}
                    onChange={(e) => {
                      editVariation({ ...variation, distribution: Number(e.target.value) });
                    }}
                    margin="normal"
                    size="small"
                    sx={{ flexGrow: { xs: 1, md: 1 }, width: { xs: "100%", md: "auto" } }}
                  />
                </Stack>
                <Stack direction="row" gap={2} alignItems="center">
                  <TextField
                    name={`assignedAttributes.${variation.id}.attributeDescription`}
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={variation.attributeDescription}
                    onChange={(e) => {
                      editVariation({ ...variation, attributeDescription: e.target.value });
                    }}
                    margin="normal"
                    size="small"
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton
                    title="Remove variation"
                    aria-label="delete"
                    color="error"
                    onClick={() => removeVariation(variation.id)}
                    sx={{ flexGrow: 0, width: "auto" }}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      <Paper variant="elevation" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems="flex-start">
          <Stack direction="row" gap={2} alignItems="center" sx={{ flex: 1, width: "100%" }}>
            <Button fullWidth variant="outlined" startIcon={<AddCircle />} onClick={() => addVariation()}>
              Add Variation
            </Button>
            <Button fullWidth variant="outlined" startIcon={<Restore />} onClick={() => resetVariations()}>
              Reset Variations
            </Button>
          </Stack>
          <Box sx={{ flex: 2, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body1" fontWeight="medium">
                Total Distribution:
              </Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                color={
                  draftVariations.reduce((sum, attr) => sum + (attr.distribution || 0), 0) !== 100
                    ? "error"
                    : "success"
                }
              >
                {draftVariations.reduce((sum, attr) => sum + (attr.distribution || 0), 0)}%
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={Math.min(
                draftVariations.reduce((sum, attr) => sum + (attr.distribution || 0), 0),
                100
              )}
              color={
                draftVariations.reduce((sum, attr) => sum + (attr.distribution || 0), 0) !== 100
                  ? "error"
                  : "success"
              }
              sx={{ height: 10, borderRadius: 1, "& .MuiLinearProgress-bar": { transitionDuration: ".2s" } }}
            />

            <Collapse in={!!fieldErrorMessage?.variationAttributes} timeout={300}>
              <Typography fontSize="small" fontStyle="italic" color="error" sx={{ mt: 1 }}>
                {fieldErrorMessage?.variationAttributes}
              </Typography>
            </Collapse>
          </Box>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          fontStyle="italic"
          sx={{ mt: 2, display: "block" }}
        >
          These settings will be the default distribution ratio for all Clients who have A/B testing enabled.
          You can edit Client-specific variation rollout settings from the{" "}
          <Link component={RouterLink} to="/abacClient" style={{ textDecoration: "none" }}>
            <strong>Client Feature Variations</strong>
          </Link>{" "}
          page.
        </Typography>
      </Paper>
    </Box>
  );
}
